import { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { DayColumn } from '../components/DayColumn.js';
import { RecipeModal } from '../components/RecipeModal.js';
import { Button } from '../components/ui/Button.js';
import { Icon } from '../components/ui/Icon.js';
import type { MealPlanResponse, MealDay, Meal, LockedMeal, BudgetSummary } from '@meal-planner/shared';

interface MealPlanRow {
  id: string;
  weekStart: string;
  planData: MealPlanResponse;
  lockedMeals: LockedMeal[] | null;
  versionHash: string;
  createdAt: string;
  cookedMeals?: Array<{ date: string; mealType: string; cookedAt: string }> | null;
}

interface ProfileData {
  budget: number | null;
  pickyEaterMode: boolean;
  familyMembers: { label: string | null; age: number }[] | null;
}

export function MealPlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [lockedMeals, setLockedMeals] = useState<Set<string>>(new Set());
  const [loadingDays, setLoadingDays] = useState<Set<string>>(new Set());
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
  const [weekRefreshing, setWeekRefreshing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [actionError, setActionError] = useState('');

  const { data, isLoading, isError } = useQuery<MealPlanRow>({
    queryKey: ['meal-plan', id],
    queryFn: () => api.get(`/api/meal-plans/${id}`),
    enabled: !!id,
  });

  const { data: profile } = useQuery<ProfileData>({
    queryKey: ['profile'],
    queryFn: () => api.get('/api/profile'),
    staleTime: 5 * 60_000,
  });

  const { data: budgetSummary } = useQuery<BudgetSummary>({
    queryKey: ['budget-summary'],
    queryFn: () => api.get('/api/me/budget-summary'),
    staleTime: 5 * 60_000,
  });

  const { data: existingList } = useQuery<{ id: string; createdAt: string } | null>({
    queryKey: ['shopping-list-for-plan', id],
    queryFn: () => api.get(`/api/shopping-lists?mealPlanId=${id}`),
    enabled: !!id,
  });

  const hasInfantInRoster = profile?.familyMembers?.some((m) => m.age === 0) ?? false;

  const { data: allergenLog = [] } = useQuery<Array<{ allergen: string; safe: boolean; introducedAt?: string }>>({
    queryKey: ['allergen-log'],
    queryFn: () => api.get('/api/allergen-log'),
    staleTime: 5 * 60_000,
    enabled: hasInfantInRoster,
  });

  useEffect(() => {
    if (data?.lockedMeals) {
      setLockedMeals(
        new Set(data.lockedMeals.map((lm) => `${lm.date}:${lm.mealType}`)),
      );
    }
  }, [data?.id]);

  // Reopen the recipe modal after returning from cook mode.
  const location = useLocation();
  const reopenRecipeId = (location.state as { reopenRecipeId?: string } | null)?.reopenRecipeId;
  useEffect(() => {
    if (!reopenRecipeId || !data?.planData?.days) return;
    for (const day of data.planData.days) {
      const match = day.meals.find((m) => m.recipeId === reopenRecipeId);
      if (match) { setSelectedMeal(match); break; }
    }
    window.history.replaceState({}, '');
  }, [reopenRecipeId, data?.planData?.days]);

  const lockMutation = useMutation({
    mutationFn: (meals: LockedMeal[]) =>
      api.put(`/api/meal-plans/${id}/locked-meals`, { lockedMeals: meals }),
  });

  const regenerateDayMutation = useMutation({
    mutationFn: (date: string) =>
      api.patch<{ day: MealDay }>(`/api/meal-plans/${id}/regenerate-day`, { date }),
    onSuccess: (res, date) => {
      qc.setQueryData<MealPlanRow>(['meal-plan', id], (old) => {
        if (!old) return old;
        return {
          ...old,
          planData: {
            days: old.planData.days.map((d) => (d.date === date ? res.day : d)),
          },
        };
      });
      setLoadingDays((prev) => { const next = new Set(prev); next.delete(date); return next; });
    },
    onError: (_err, date) => {
      setLoadingDays((prev) => { const next = new Set(prev); next.delete(date); return next; });
      setActionError('Failed to refresh day. Please try again.');
    },
  });

  const regenerateWeekMutation = useMutation({
    mutationFn: () =>
      api.patch<{ plan: MealPlanResponse }>(`/api/meal-plans/${id}/regenerate-week`, {}),
    onSuccess: (res) => {
      qc.setQueryData<MealPlanRow>(['meal-plan', id], (old) =>
        old ? { ...old, planData: res.plan } : old,
      );
      setWeekRefreshing(false);
    },
    onError: () => {
      setWeekRefreshing(false);
      setActionError('Failed to refresh week. Please try again.');
    },
  });

  const createShare = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/shared-links', {
        resourceType: 'meal_plan',
        resourceId: id,
        expiresInDays: 7,
      }),
    onSuccess: (res) => setShareUrl(res.url),
    onError: (err: any) => setActionError(err.body?.error ?? 'Failed to create share link.'),
  });

  function toggleLock(date: string, mealType: string) {
    const key = `${date}:${mealType}`;
    setLockedMeals((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      const meals: LockedMeal[] = Array.from(next).map((k) => {
        const colonIdx = k.indexOf(':');
        return {
          date: k.slice(0, colonIdx),
          mealType: k.slice(colonIdx + 1) as 'breakfast' | 'lunch' | 'dinner',
        };
      });
      lockMutation.mutate(meals);
      return next;
    });
  }

  function handleRefreshDay(date: string) {
    setActionError('');
    setLoadingDays((prev) => new Set(prev).add(date));
    regenerateDayMutation.mutate(date);
  }

  function handleRefreshWeek() {
    setActionError('');
    setWeekRefreshing(true);
    regenerateWeekMutation.mutate();
  }

  const hasBLW = profile?.familyMembers?.some((m) => m.age === 0) ?? false;
  const hasBudget = !!profile?.budget;
  const pickyEaterMode = profile?.pickyEaterMode ?? false;

  if (isLoading) {
    return (
      <Layout>
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="space-y-2 animate-pulse">
              <div className="h-14 rounded-lg" style={{ background: 'var(--surface-2)' }} />
              <div className="h-[88px] rounded-lg" style={{ background: 'var(--surface-2)' }} />
              <div className="h-[88px] rounded-lg" style={{ background: 'var(--surface-2)' }} />
              <div className="h-[88px] rounded-lg" style={{ background: 'var(--surface-2)' }} />
            </div>
          ))}
        </div>
      </Layout>
    );
  }

  if (isError || !data) {
    return (
      <Layout>
        <p className="text-sm" style={{ color: 'var(--danger, #e11d48)' }}>Plan not found.</p>
      </Layout>
    );
  }

  const plan = data.planData;
  // G2 Area 3 — index swap entries by replacement recipeId for quick lookup
  const budgetSwapMap = new Map<string, { originalName?: string }>();
  for (const entry of plan.swapLog ?? []) {
    budgetSwapMap.set(entry.replacementRecipeId, { originalName: entry.originalRecipeName });
  }
  const budgetSwapRecipeIds = new Set(budgetSwapMap.keys());
  const todayStr = new Date().toISOString().slice(0, 10);
  const planDates = plan.days.map((d) => d.date).sort();
  const planStatus: 'past' | 'current' | 'future' =
    planDates.length === 0
      ? 'current'
      : planDates[planDates.length - 1]! < todayStr
        ? 'past'
        : planDates[0]! > todayStr
          ? 'future'
          : 'current';
  const isPastPlan = planStatus === 'past';
  const weekStart = new Date(data.weekStart + 'T12:00:00');
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const monthLabel = weekStart.toLocaleDateString('en-US', { month: 'long' });
  const rangeLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  const refreshing = weekRefreshing || regenerateWeekMutation.isPending;

  return (
    <Layout>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-[0.16em] uppercase mb-2" style={{ color: 'var(--accent)' }}>
            {monthLabel} · weekly plan
          </div>
          <h1 className="h-display" style={{ margin: 0, fontSize: 44, lineHeight: 1.05, color: 'var(--ink)' }}>
            Week of <span className="h-serif" style={{ fontStyle: 'italic', color: 'var(--accent)' }}>{rangeLabel}</span>
          </h1>
          {hasBudget && (
            <div className="mt-3 inline-flex items-center gap-1.5 chip" style={{ background: 'var(--accent-soft)', color: 'var(--accent)', borderColor: 'transparent' }}>
              <Icon name="wallet" size={12} />
              ${profile!.budget}/week budget
              {budgetSummary && budgetSummary.pricedItemCount > 0 && (
                existingList ? (
                  <Link
                    to={`/shopping-list/${existingList.id}`}
                    className="underline"
                    style={{ color: 'var(--accent)' }}
                  >
                    (${budgetSummary.currentPlanCost.toFixed(2)} estimated cost)
                  </Link>
                ) : (
                  <span>(${budgetSummary.currentPlanCost.toFixed(2)} estimated cost)</span>
                )
              )}
            </div>
          )}
          {isPastPlan && (
            <div
              className="mt-3 text-[10px] font-semibold tracking-[0.16em] uppercase"
              style={{ color: 'var(--ink-3)' }}
            >
              Reference only
            </div>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {!isPastPlan && (
            <Button variant="primary" onClick={handleRefreshWeek} disabled={refreshing}>
              <Icon name="refresh" size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh week'}
            </Button>
          )}

          {existingList ? (
            <>
              <Link to={`/shopping-list/${existingList.id}`} className="btn btn-soft" style={{ padding: '12px 20px' }}>
                <Icon name="cart" size={14} />
                View shopping list
                <Icon name="arrow" size={13} />
              </Link>
              {!isPastPlan && (
                <Button variant="ghost" onClick={() => navigate(`/ingredient-checklist/${id}`)}>
                  Change what I have
                </Button>
              )}
            </>
          ) : (
            !isPastPlan && (
              <Button variant="ghost" onClick={() => navigate(`/ingredient-checklist/${id}`)}>
                <Icon name="cart" size={14} />
                Review ingredients
              </Button>
            )
          )}

          <Button variant="ghost" onClick={() => createShare.mutate()} disabled={createShare.isPending}>
            <Icon name="share" size={14} />
            Share
          </Button>
        </div>
      </div>

      {hasBLW && (
        <div
          className="card flex items-start gap-3 px-5 py-4 mb-5 text-sm"
          style={{ background: 'var(--accent-soft)', borderColor: 'transparent' }}
        >
          <Icon name="family" size={16} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ color: 'var(--ink-2)' }}>
            <span className="font-semibold" style={{ color: 'var(--accent)' }}>BLW active.</span>{' '}
            Set portions aside <strong style={{ color: 'var(--ink)' }}>before</strong> adding salt, spice, or seasoning for your infant.{' '}
            <Link to="/profile#allergens" className="underline" style={{ color: 'var(--accent)' }}>
              Track allergens →
            </Link>
          </div>
        </div>
      )}

      {shareUrl && (
        <div className="card px-5 py-4 mb-5" style={{ background: 'var(--surface-2)' }}>
          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>
            Share link created · expires in 7 days
          </p>
          <code className="text-xs break-all" style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
            {shareUrl}
          </code>
        </div>
      )}

      {actionError && (
        <p className="text-sm mb-4" style={{ color: 'var(--danger, #e11d48)' }}>{actionError}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-7 gap-3">
        {plan.days.map((day) => (
          <DayColumn
            key={day.date}
            day={day}
            isLoading={loadingDays.has(day.date)}
            lockedMeals={lockedMeals}
            onToggleLock={toggleLock}
            onRefreshDay={handleRefreshDay}
            onMealClick={setSelectedMeal}
            showPickyTags={pickyEaterMode}
            isRefreshingWeek={weekRefreshing}
            showBLWCards={hasBLW}
            familyMembers={profile?.familyMembers ?? []}
            allergenLog={allergenLog}
            budgetSwapRecipeIds={budgetSwapRecipeIds}
            planId={id}
            cookedMeals={data?.cookedMeals ?? []}
          />
        ))}
      </div>

      <RecipeModal
        meal={selectedMeal}
        onClose={() => setSelectedMeal(null)}
        budgetSwapInfo={
          selectedMeal?.recipeId && budgetSwapMap.has(selectedMeal.recipeId)
            ? {
                originalName: budgetSwapMap.get(selectedMeal.recipeId)?.originalName,
                weeklyBudget: profile?.budget ?? null,
              }
            : undefined
        }
      />
    </Layout>
  );
}
